import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Download } from 'lucide-react'
import { CopyButton } from '../../../../pages/vulnerabilities/components/VulnerabilityClassification'

export interface RecoveryCodesProps {
  codes: string[]
  /** Called once the user confirms they saved the codes. */
  onAcknowledge: () => void
  acknowledgeLabel: string
}

/**
 * The recovery codes, shown ONCE: copy, download, and an explicit "I saved them"
 * before the flow may continue. The codes are secrets — rendered, copied and
 * downloaded, never logged, never put in storage.
 */
export const RecoveryCodes: React.FC<RecoveryCodesProps> = ({ codes, onAcknowledge, acknowledgeLabel }) => {
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({ id: `TWO_FACTOR.${id}` })
  const [saved, setSaved] = useState(false)
  const text = codes.join('\n')

  const download = () => {
    const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'recovery-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className='d-flex flex-column gap-4 text-start'>
      <div className='alert alert-warning mb-0' role='alert'>
        <div className='fw-bold'>{t('recovery.warningTitle')}</div>
        {t('recovery.warning')}
      </div>
      <ul
        aria-label={t('recovery.listLabel')}
        dir='ltr'
        className='list-unstyled m-0 p-4 border rounded bg-light'
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem' }}
      >
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ul>
      <div className='d-flex flex-wrap gap-2 align-items-center'>
        <CopyButton value={text} label={t('recovery.copy')} />
        <button type='button' className='btn btn-sm btn-light-primary d-flex align-items-center gap-2' onClick={download}>
          <Download size={15} aria-hidden='true' />
          {t('recovery.download')}
        </button>
      </div>
      <div className='form-check'>
        <input
          id='two-factor-recovery-saved'
          className='form-check-input'
          type='checkbox'
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
        />
        <label className='form-check-label' htmlFor='two-factor-recovery-saved'>
          {t('recovery.saved')}
        </label>
      </div>
      <button type='button' className='btn btn-primary' disabled={!saved} onClick={onAcknowledge}>
        {acknowledgeLabel}
      </button>
    </div>
  )
}
